import L, { type LatLngBoundsExpression, type LatLngTuple } from 'leaflet'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import React, { type ReactNode } from 'react'
import {
	MapContainer,
	Marker,
	Polyline,
	Popup,
	ScaleControl,
	TileLayer,
} from 'react-leaflet'
import { type TooltipX } from './elevation-chart.tsx'

const startMarker = L.icon({
	iconUrl: '/img/home-sharp.svg',
	iconSize: [25, 41],
	iconAnchor: [12, 41],
	popupAnchor: [1, -34],
	tooltipAnchor: [16, -28],
	shadowSize: [41, 41],
})

const endMarker = L.icon({
	iconUrl: '/img/stop-sharp.svg',
	iconSize: [25, 41],
	iconAnchor: [12, 41],
	popupAnchor: [1, -34],
	tooltipAnchor: [16, -28],
	shadowSize: [41, 41],
})

const currentMarker = L.icon({
	iconUrl: '/img/image-sharp.svg',
	iconSize: [25, 41],
	iconAnchor: [12, 20],
	popupAnchor: [1, -34],
	tooltipAnchor: [16, -28],
})

const blipMarker = L.icon({
	iconUrl: '/img/search-sharp.svg',
	iconSize: [30, 30],
	iconAnchor: [15, 15],
})

const trailMarker = L.icon({
	iconUrl: markerIcon,
	iconRetinaUrl: markerIcon2x,
	shadowUrl: markerShadow,
	iconSize: [25, 41],
	iconAnchor: [12, 41],
	popupAnchor: [1, -34],
	tooltipAnchor: [16, -28],
	shadowSize: [41, 41],
})

const TYPE_TO_MARKER: { [key: string]: L.Icon } = {
	start: startMarker,
	end: endMarker,
	current: currentMarker,
	blip: blipMarker,
	trail: trailMarker,
}

export type MarkerData = {
	id: string
	position: LatLngTuple
	popup: ReactNode
	alt: string
	title: string
	type: string
}

interface LeafletMapProps {
	className?: string
	center: LatLngTuple
	bounds: LatLngBoundsExpression
	positions: { href: string; latLng: LatLngTuple; index: number }[]
	markers: MarkerData[]
	blip?: TooltipX | null | undefined
	navigateTo: (href: string) => void
}

const LeafletMap: React.FC<LeafletMapProps> = ({
	className,
	center,
	bounds,
	markers,
	positions,
	blip = null,
	navigateTo,
}) => {
	const polyLine = positions.map((position, index, source) => {
		if (index === source.length - 1) return null

		return (
			<React.Fragment key={`${position.href}-${position.index}`}>
				<Polyline
					positions={[position.latLng, source[index + 1]!.latLng]}
					weight={12}
					color="transparent"
					interactive={true}
					className="hit-target"
					eventHandlers={{
						click: () => navigateTo(position.href),
					}}
				/>
				<Polyline
					positions={[position.latLng, source[index + 1]!.latLng]}
					className={`color-feature`}
					weight={3}
					interactive={false}
				/>
			</React.Fragment>
		)
	})

	return (
		<MapContainer
			aria-label="Map area"
			bounds={bounds}
			center={center}
			className={className}
			style={{ paddingBottom: '20px' }} // Add some padding at the bottom to ensure attribution is visible
		>
			<TileLayer
				attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
				referrerPolicy="strict-origin-when-cross-origin"
				url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
			/>
			{markers.filter(Boolean).map((marker) => (
				<Marker
					key={marker.id}
					position={marker.position}
					icon={TYPE_TO_MARKER[marker.type]}
					title={marker.title}
					alt={marker.alt}
				>
					<Popup>{marker.popup}</Popup>
				</Marker>
			))}
			{blip && <Marker position={blip.latLng} icon={blipMarker} />}
			{polyLine || null}
			<ScaleControl imperial={false} />
		</MapContainer>
	)
}

export default LeafletMap
